import React, { useState } from 'react';
import { 
  ComposedChart, 
  Bar, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';
import { CalendarDaysIcon } from '@heroicons/react/24/outline';
import Card from '../common/Card';
import { ServiceIcon } from './calendarHelpers';

interface ModulationChartCardProps {
  chartData: any[];
  selectedService: 'electricity' | 'water' | 'steam' | 'co2';
  setSelectedService: (service: 'electricity' | 'water' | 'steam' | 'co2') => void;
  chartViewMode: 'day' | 'week' | 'month';
  setChartViewMode: (mode: 'day' | 'week' | 'month') => void;
  chartDayFocus: string;
  setChartDayFocus: (day: string) => void;
  getServiceLabel: () => string;
  calendarView: 'month' | 'week';
}

export const ModulationChartCard: React.FC<ModulationChartCardProps> = ({
  chartData,
  selectedService,
  setSelectedService,
  chartViewMode,
  setChartViewMode,
  chartDayFocus,
  setChartDayFocus,
  getServiceLabel,
  calendarView
}) => {
  const [hiddenSeries, setHiddenSeries] = useState<Record<string, boolean>>({});

  const handleLegendClick = (o: any) => {
    const { dataKey } = o;
    if (dataKey) {
      setHiddenSeries(prev => ({
        ...prev,
        [dataKey]: !prev[dataKey]
      }));
    }
  };

  return (
    <div className="space-y-6">
      {/* Selector de Servicio */}
      <div className="flex flex-wrap bg-gray-100 p-1 rounded-xl w-max gap-1">
        {(['electricity', 'water', 'steam', 'co2'] as const).map(s => (
          <button
            key={s}
            onClick={() => setSelectedService(s)}
            className={`px-4 py-2 text-sm font-bold rounded-lg transition-all flex items-center gap-1.5 ${
              selectedService === s ? 'bg-white shadow-sm text-[#E2B53F]' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <ServiceIcon type={s} className="h-4 w-4" />
            <span>{s === 'electricity' ? 'Energía' : s === 'water' ? 'Agua' : s === 'steam' ? 'Vapor' : 'CO2'}</span>
          </button>
        ))}
      </div>

      <Card className="overflow-hidden p-0 shadow-sm border border-gray-100">
        {/* Cabecera del Gráfico con filtros */}
        <div className="p-4 border-b border-gray-100 bg-white flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Modulación y Predicción de Suministro</h3>
            <p className="text-sm text-gray-500">Muestra la predicción ajustada por cortes de servicio contra la medición real de la planta.</p>
          </div>
          
          <div className="flex flex-wrap items-center gap-2">
            {/* Selector de Día si modo día está activo */}
            {chartViewMode === 'day' && (
              <input
                type="date"
                value={chartDayFocus}
                onChange={(e) => setChartDayFocus(e.target.value)}
                className="bg-slate-50 border border-gray-200 text-xs font-bold text-gray-700 px-3 py-1.5 rounded-lg focus:ring-1 focus:ring-[#E2B53F] outline-none"
              />
            )}

            {/* Botones de Selector de Modo del Gráfico */}
            <div className="flex bg-slate-100 p-0.5 rounded-lg border border-slate-200">
              {(['day', 'week', 'month'] as const).map(mode => (
                <button
                  key={`chart-mode-${mode}`}
                  onClick={() => setChartViewMode(mode)}
                  className={`px-3 py-1 text-xs font-extrabold rounded-md uppercase tracking-wider transition-all ${
                    chartViewMode === mode 
                      ? 'bg-white text-slate-900 shadow-sm' 
                      : 'text-slate-500 hover:text-slate-800'
                  }`}
                >
                  {mode === 'day' ? 'Día' : mode === 'week' ? 'Semana' : 'Mes'}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Gráfico Recharts */}
        <div className="h-[450px] w-full bg-[#27272A] p-6 border border-[#3f3f46] relative overflow-hidden">
          {chartData.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center text-gray-400">
              <CalendarDaysIcon className="h-12 w-12 text-gray-500 mb-2" />
              <p className="font-bold">No hay planificación cargada</p>
              <p className="text-sm text-gray-500">Carga hectolitros y eventos en el Calendario Operativo primero.</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={chartData}
                margin={{ top: 20, right: 30, left: 20, bottom: 40 }}
                barGap={1}
                barCategoryGap="20%"
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#3f3f46" />
                <XAxis
                  dataKey="day"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 11, fill: '#d1d5db' }}
                  dy={15}
                />
                <YAxis
                  yAxisId="left"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: '#d1d5db' }}
                  label={{ value: getServiceLabel(), angle: -90, position: 'insideLeft', offset: -5, fill: '#d1d5db', fontSize: 11, fontWeight: 'bold' }}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: '#a1a1aa' }}
                  label={{ value: 'Producción (hl)', angle: 90, position: 'insideRight', offset: -5, fill: '#a1a1aa', fontSize: 11, fontWeight: 'bold' }}
                />
                <Tooltip
                  content={({ active, payload, label }: any) => {
                    if (active && payload && payload.length) {
                      const p = payload[0].payload;
                      const unit = getServiceLabel().split(' ')[1] || '';
                      return (
                        <div className="bg-[#181b1f] border border-[#2c3035] p-4 rounded-xl shadow-2xl space-y-2 text-xs">
                          <p className="font-bold text-gray-200 border-b border-[#2c3035] pb-1.5 mb-1.5">{chartViewMode === 'day' ? `${p.date} a las ${label}` : chartViewMode === 'month' ? `Día ${label}` : label}</p>
                          <p className="text-cyan-400 font-black">Consumo en Predicción: {p.predConsumo} <span className="text-gray-400 text-[10px]">{unit}</span></p>
                          <p className="text-amber-400 font-black">Consumo Real: {p.realConsumo} <span className="text-gray-400 text-[10px]">{unit}</span></p>
                          {p.corteHoras > 0 && <p className="text-red-400 font-bold">Cortes de Suministro: {p.corteHoras} {chartViewMode === 'day' ? 'activo' : 'hrs'}</p>}
                          <p className="text-gray-300">Prod. Planificada: {p['Prod. Proyectada (hl)']} hl</p>
                          <p className="text-gray-300">Prod. Real: {p['Prod. Real (hl)']} hl</p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Legend 
                  verticalAlign="top" 
                  height={36} 
                  iconType="circle" 
                  wrapperStyle={{ color: '#f3f4f6', cursor: 'pointer' }} 
                  onClick={handleLegendClick}
                />
                
                {/* Hectolitros planificados y reales */}
                <Bar yAxisId="right" dataKey="Prod. Real (hl)" name="Producción Real (hl)" fill="#ffb700" opacity={0.3} radius={[4, 4, 0, 0]} maxBarSize={30} hide={!!hiddenSeries['Prod. Real (hl)']} />
                <Bar yAxisId="right" dataKey="Prod. Proyectada (hl)" name="Producción Proyectada (hl)" fill="#3b82f6" opacity={0.15} radius={[4, 4, 0, 0]} maxBarSize={30} hide={!!hiddenSeries['Prod. Proyectada (hl)']} />

                {/* Predicción (Línea punteada cian) */}
                <Line yAxisId="left" type="monotone" dataKey="predConsumo" name="Consumo en Predicción" stroke="#00E5FF" strokeWidth={2.5} strokeDasharray="6 4" dot={{ r: 4, fill: '#00E5FF', strokeWidth: 1 }} activeDot={{ r: 6 }} hide={!!hiddenSeries['predConsumo']} />

                {/* Consumo real (Solid Teal for clear high contrast) */}
                <Bar yAxisId="left" dataKey="realConsumo" name="Consumo Real Medido" fill="#0D9488" radius={[4, 4, 0, 0]} maxBarSize={20} hide={!!hiddenSeries['realConsumo']} />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </div>
      </Card>
    </div>
  );
};
